/**
 * Analytics tracking utility for the public booking frontend.
 *
 * Currently logs events to console for development.
 * Ready for Google Analytics / Plausible integration.
 *
 * IMPORTANT: No PII (Personally Identifiable Information) in analytics events.
 * Never include names, emails, phone numbers, or other identifying data.
 */

// Event types for type safety
export type PageViewEvent =
  | 'search_home'
  | 'trip_results'
  | 'seat_selection'
  | 'checkout'
  | 'payment'
  | 'confirmation'
  | 'ticket_view';

export type TrackingEvent =
  | 'search_initiated'
  | 'trip_selected'
  | 'trip_stops_expanded'
  | 'seats_selected'
  | 'checkout_started'
  | 'payment_attempted'
  | 'booking_completed';

// Event payloads (no PII)
export interface SearchInitiatedPayload {
  originCityId: number;
  destinationCityId: number;
  tripType: 'one-way' | 'round-trip';
  passengerCount: number;
}

export interface TripSelectedPayload {
  tripId: number;
  routeId: number;
  price: number;
}

export interface TripStopsExpandedPayload {
  tripId: number;
}

export interface SeatsSelectedPayload {
  tripId: number;
  seatCount: number;
}

export interface CheckoutStartedPayload {
  tripId: number;
  seatCount: number;
  totalPrice: number;
  hasDiscount: boolean;
}

export interface PaymentAttemptedPayload {
  tripId: number;
  seatCount: number;
  totalPrice: number;
  discountApplied: boolean;
}

export interface BookingCompletedPayload {
  tripId: number;
  seatCount: number;
  totalPrice: number;
  discountAmount: number;
}

type EventPayloadMap = {
  search_initiated: SearchInitiatedPayload;
  trip_selected: TripSelectedPayload;
  trip_stops_expanded: TripStopsExpandedPayload;
  seats_selected: SeatsSelectedPayload;
  checkout_started: CheckoutStartedPayload;
  payment_attempted: PaymentAttemptedPayload;
  booking_completed: BookingCompletedPayload;
};

// Check if we're in development mode
const isDev = import.meta.env.DEV;

// Analytics configuration (for future provider integration)
interface AnalyticsConfig {
  enabled: boolean;
  debug: boolean;
}

const config: AnalyticsConfig = {
  enabled: true,
  debug: isDev,
};

/**
 * Track a page view event.
 * Called when a user navigates to a new page.
 *
 * @param page - The page identifier (no PII)
 * @param tenantSlug - The tenant subdomain
 */
export function trackPageView(page: PageViewEvent, tenantSlug: string): void {
  if (!config.enabled) return;

  const event = {
    type: 'page_view',
    page,
    tenant: tenantSlug,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    referrer: document.referrer || undefined,
  };

  if (config.debug) {
    console.log('[Analytics] Page View:', event);
  }

  // Future integration points:
  // - Google Analytics 4: gtag('event', 'page_view', { page_title: page })
  // - Plausible: plausible('pageview', { props: { page } })
}

/**
 * Track a custom event with typed payload.
 * Used for tracking user actions in the booking funnel.
 *
 * @param event - The event name
 * @param payload - Event-specific data (no PII)
 */
export function trackEvent<T extends TrackingEvent>(
  event: T,
  payload: EventPayloadMap[T]
): void {
  if (!config.enabled) return;

  const eventData = {
    type: 'event',
    event,
    payload,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
  };

  if (config.debug) {
    console.log('[Analytics] Event:', eventData);
  }

  // Future integration points:
  // - Google Analytics 4: gtag('event', event, payload)
  // - Plausible: plausible(event, { props: payload })
}

/**
 * Enable or disable analytics tracking.
 * Useful for GDPR/CCPA compliance.
 */
export function setAnalyticsEnabled(enabled: boolean): void {
  config.enabled = enabled;
  if (config.debug) {
    console.log('[Analytics] Enabled:', enabled);
  }
}

/**
 * Enable or disable debug logging.
 */
export function setAnalyticsDebug(debug: boolean): void {
  config.debug = debug;
}
